import { DimensionUnit } from '../../enums/dimension-unit';
import { ProductTypes } from '../../enums/product-types';
import { WeightUnit } from '../../enums/weight-unit';
import { convertDimensions } from '../../services/unit-services/dimensions/dimension-convert-service';
import { convertFromDimensionUnitEnum } from '../../services/unit-services/dimensions/dimension-unit-enum-converter';
import { convertWeight } from '../../services/unit-services/weights/weight-convert-service';
import { convertFromWeightUnitEnum } from '../../services/unit-services/weights/weight-unit-enum-converter';

export const getProductTypeDisplayNameAndHeaderText = (productType) => {
  const enumObj = ProductTypes[productType] || ProductTypes.Standalone;

  return {
    headerText: enumObj.productDetailHeaderText,
    productTypeText: enumObj.displayText,
  };
};

export const convertWeightToDisplayUnit = (sellerWeightUnitSetting, weight, weightUnit) => {
  if (weight == null) {
    return {
      weight: undefined,
      weightUnit: convertFromWeightUnitEnum(sellerWeightUnitSetting),
    };
  }

  const { value, unit } = convertWeight({ value: weight, unit: weightUnit }, sellerWeightUnitSetting);
  const formattedValue = Number.isInteger(value) ? value : Number.parseFloat(value.toFixed(3));

  return {
    weight: formattedValue,
    weightUnit: convertFromWeightUnitEnum(unit),
  };
};

const roundToDisplayPrecision = (value) => (Number.isInteger(value) ? value : Number.parseFloat(value.toFixed(3)));

// Item measurements keep their own stored unit, so they need the same normalization the package
// fields get above — otherwise they read in a different unit than the rest of the app.
// Without a stored unit there is nothing to convert from, so the value and unit are left alone and
// the input falls back to the seller's display unit.
export const convertItemWeightToDisplayUnit = (sellerWeightUnitSetting, itemWeight, itemWeightUnit) => {
  const hasStoredUnit = !!itemWeightUnit && itemWeightUnit !== WeightUnit.Unknown;

  if (itemWeight == null || !hasStoredUnit) {
    return {
      itemWeight,
      itemWeightUnitOfMeasureId: itemWeightUnit,
    };
  }

  const { value, unit } = convertWeight({ value: itemWeight, unit: itemWeightUnit }, sellerWeightUnitSetting);

  return {
    itemWeight: roundToDisplayPrecision(value),
    itemWeightUnitOfMeasureId: convertFromWeightUnitEnum(unit),
  };
};

export const convertItemDimensionsToDisplayUnit = (sellerDimensionUnitSetting, itemDimensionsWithUnit) => {
  const { length, width, height, unit } = itemDimensionsWithUnit;
  const hasStoredUnit = !!unit && unit !== DimensionUnit.Unknown;

  if (!hasStoredUnit) {
    return {
      itemLength: length,
      itemWidth: width,
      itemHeight: height,
      itemDimensionUnitOfMeasureId: unit,
    };
  }

  const converted = convertDimensions(itemDimensionsWithUnit, sellerDimensionUnitSetting);

  return {
    itemLength: roundToDisplayPrecision(converted.length),
    itemWidth: roundToDisplayPrecision(converted.width),
    itemHeight: roundToDisplayPrecision(converted.height),
    itemDimensionUnitOfMeasureId: convertFromDimensionUnitEnum(converted.unit),
  };
};

export const convertDimensionToDisplayUnit = (sellerDimensionUnitSetting, dimensionsWithUnit) => {
  const { length, width, height, unit } = convertDimensions(dimensionsWithUnit, sellerDimensionUnitSetting);

  return {
    convertedLength: length,
    convertedWidth: width,
    convertedHeight: height,
    sizeUnit: convertFromDimensionUnitEnum(unit),
  };
};

export const locationNameFromParts = (location) => {
  const { area, unit, shelf, bin } = location;
  const parts = [area, unit, shelf, bin].filter((part) => part && part.length > 0);
  return parts.join(' - ');
};
